import * as React from 'react';
import { useState } from 'react';

import { CheckCircleOutlined } from '@ant-design/icons';
import { useBoolean } from 'ahooks';
import {
  Button,
  Divider,
  Form,
  FormInstance,
  notification,
  Typography,
} from 'antd';

import { AntdUpload, DownloadOutlinedIcon } from 'components';

import { exportProducts } from '../../../../../services';
import {
  importProducts,
  ImportResult,
} from '../../../../../services/product/import-products';
import { AntdUploadChangeParam, FileInfo } from '../../../../../types';
import {
  formatFileLocaleUnitSize,
  generateDownloadFile,
  normalizeToString,
  safeParseBoolean,
} from '../../../../../utils';
import { FieldType } from '../index';

interface ProductsExportActionProps {
  form: FormInstance;
  durationRange: [string?, string?];
}

const ProductsExportAction = ({
  form,
  durationRange,
}: ProductsExportActionProps) => {
  const [loading, { toggle: toggleLoading }] = useBoolean();
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);

  const handleExport = async () => {
    if (loading) return;

    const {
      searchKey = '',
      statuses = [],
      brandsIds = [],
      categoriesIds = [],
      callForPrice,
      isIndex,
    } = form.getFieldsValue() as FieldType;

    const [lowerBoundCreateDate, upperBoundCreateDate] = durationRange;

    const params: Parameters<typeof exportProducts>[0] = {
      searchKey,
      statuses,
      brandsIds,
      lowerBoundCreateDate,
      upperBoundCreateDate,
      sortType: 'Newest',
      isIndex: safeParseBoolean(isIndex),
      callForPrice: safeParseBoolean(callForPrice),
      categoriesIds: categoriesIds.map(({ value }) => normalizeToString(value)),
    };

    toggleLoading();
    exportProducts(params)
      .then(fileInfo => {
        setFileInfo(fileInfo);
        notification.success({
          message: 'گزارش محصولات با موفقیت ایجاد شد.',
        });
      })
      .catch(message => {
        notification.error({ message });
      })
      .finally(() => {
        toggleLoading();
      });
  };

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          بارگیری گزارش‌
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          از طریق این بخش می‌توانید گزارش محصول‌های انتخاب شده را بارگیری کنید.
        </Typography.Paragraph>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        <Form.Item>
          <Button
            block
            loading={loading}
            onClick={handleExport}
            type='primary'
            htmlType='submit'
            icon={<DownloadOutlinedIcon />}
          >
            بارگیری اطلاعات
          </Button>
        </Form.Item>
      </div>

      {fileInfo && (
        <div className='mx-6 mb-4 rounded-lg border border-green-200 bg-green-50 p-4'>
          <div className='mb-2 flex items-center gap-2'>
            <CheckCircleOutlined className='text-green-600' />
            <Typography.Text strong className='text-green-800'>
              فایل آماده دانلود است
            </Typography.Text>
          </div>
          <div className='mb-3 text-sm text-gray-600'>
            <div>نام فایل: {fileInfo.name}</div>
            <div>حجم: {formatFileLocaleUnitSize(fileInfo.size)}</div>
          </div>
          <Button
            type='link'
            block
            size='middle'
            icon={<DownloadOutlinedIcon />}
            href={generateDownloadFile('BulkProducts', fileInfo.id)}
          >
            دریافت فایل
          </Button>
        </div>
      )}
    </div>
  );
};

const ProductsImportAction = () => {
  const [fileId, setFileId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [importResult, setImportResult] = React.useState<ImportResult | null>(
    null,
  );

  const handleUploadChange = ({
    file: { status, response },
  }: AntdUploadChangeParam) => {
    if (status === 'done' && response?.id) {
      setFileId(response.id);
      setImportResult(null);
      notification.success({ message: 'فایل با موفقیت بارگذاری شد' });
    }
  };

  const handleImport = async () => {
    if (!fileId) {
      notification.warning({ message: 'لطفاً ابتدا فایل را بارگذاری کنید' });
      return;
    }

    if (loading) return;

    setLoading(true);

    importProducts({ fileId: fileId })
      .then(result => {
        setImportResult(result);
        notification.success({
          message: 'بارگذاری اطلاعات با موفقیت انجام شد',
        });
      })
      .catch(message => {
        notification.error({ message });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          بارگذاری گزارش‌
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          از طریق این بخش می‌توانید گزارش محصول‌ها را بارگذاری کنید. بر اساس
          تغییرات شما، محصولات در سایت بروزرسانی می‌شوند.
        </Typography.Paragraph>
      </div>

      <div className='px-6'>
        <Form.Item>
          <AntdUpload
            withSheet
            withDocuments={false}
            withPictures={false}
            withVideos={false}
            withAudios={false}
            ownerType={'BulkProducts'}
            onChange={handleUploadChange}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        <Form.Item>
          <Button
            block
            type='primary'
            loading={loading}
            disabled={!fileId}
            onClick={handleImport}
            icon={<DownloadOutlinedIcon />}
          >
            بارگذاری اطلاعات
          </Button>
        </Form.Item>
      </div>

      {importResult && (
        <div className='mx-6 mb-6'>
          <div className='mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4'>
            <Typography.Title level={5} className='mb-3 text-blue-700'>
              خلاصه نتیجه
            </Typography.Title>
            <div className='space-y-2 text-sm'>
              <div className='flex justify-between'>
                <span>تعداد کل ردیف‌ها:</span>
                <span className='font-medium'>{importResult.totalRow}</span>
              </div>
              <div className='flex justify-between'>
                <span>محصول‌های تغییر یافته:</span>
                <span className='font-medium text-green-600'>
                  {importResult.totalProductChanged}
                </span>
              </div>
              <div className='flex justify-between'>
                <span>مدل‌های تغییر یافته:</span>
                <span className='font-medium text-green-600'>
                  {importResult.totalSubProductChanged}
                </span>
              </div>
              <div className='flex justify-between'>
                <span>تعداد خطاها:</span>
                <span
                  className={`font-medium ${importResult.errors.length > 0 ? 'text-red-600' : 'text-green-600'}`}
                >
                  {importResult.errors.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {importResult && importResult.errors.length > 0 && (
        <div className='mx-6 mb-6 rounded-lg border border-red-200 bg-red-50 p-4'>
          <Typography.Title level={5} className='mb-3 text-red-700'>
            خطاهای موجود در فایل
          </Typography.Title>

          <div className='space-y-2 text-sm'>
            {importResult.errors.map((err, index) => (
              <div
                key={index}
                className='rounded-md border border-red-300 bg-white px-3 py-2 shadow-sm'
              >
                <div className='flex gap-2'>
                  <span>ردیف:</span>
                  <span className='font-medium'>{err.row}</span>
                </div>
                <div className='mt-1'>
                  <span className='font-medium' dir={'rtl'}>
                    {err.message}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { ProductsExportAction, ProductsImportAction };
